/* ==========================================================================
   TSH · Header — lógica
   The Sole Hunters · Tema Tinker (Horizon)
   --------------------------------------------------------------------------
   Custom element <tsh-header>. Se auto-registra.
   Depende de: assets/tsh-header.css
   Escucha:  cart:update · cart:refresh · cart:change · tsh:wishlist:changed
   Emite:    tsh:header:ready
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('tsh-header')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var MQ_DESKTOP = '(min-width: 990px)';
  var WISH_KEY = 'tsh:wishlist';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function isDesktop() { return window.matchMedia(MQ_DESKTOP).matches; }

  class TshHeader extends HTMLElement {

    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.pill    = this.querySelector('.tsh-hd__pill');
      this.bar     = this.querySelector('.tsh-bar');
      this.spacer  = document.querySelector('[data-header-spacer]');
      this.veil    = document.querySelector('[data-veil]');
      this.burger  = this.querySelector('[data-burger]');
      this.rows    = Array.prototype.slice.call(this.querySelectorAll('[data-row]'));

      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.fallbackPx   = parseInt(this.dataset.fallbackThreshold, 10) || 40;
      this.lastY        = window.scrollY;
      this.openRow      = null;
      this.cleanup      = [];

      this.safe('measure',   this.initMeasure);
      this.safe('sentinel',  this.initSentinel);
      this.safe('rows',      this.initRows);
      this.safe('dropdowns', this.initDropdowns);
      this.safe('lang',      this.initLang);
      this.safe('cart',      this.initCart);
      this.safe('wishlist',  this.initWishlist);

      document.dispatchEvent(new CustomEvent('tsh:header:ready', { detail: { el: this } }));
    }

    disconnectedCallback() {
      this.cleanup.forEach(function (fn) { try { fn(); } catch (e) {} });
      this.cleanup = [];
      document.body.classList.remove('tsh-locked');
      document.body.style.removeProperty('padding-right');

      // Los paneles viven en el body: hay que retirarlos a mano
      if (this._panels) {
        this._panels.forEach(function (p) { if (p.parentNode) p.parentNode.removeChild(p); });
        this._panels = null;
      }
    }

    safe(name, fn) {
      try { fn.call(this); }
      catch (err) { console.error('[TSH header] ' + name, err); }
    }

    on(target, type, handler, opts) {
      target.addEventListener(type, handler, opts);
      this.cleanup.push(function () { target.removeEventListener(type, handler, opts); });
    }

    /* ======================================================================
       MEDICIÓN
       ====================================================================== */
    initMeasure() {
      var self = this;

      this.measure = function () {
        var h = self.offsetHeight;
        var barH = self.bar ? self.bar.getBoundingClientRect().bottom : h;
        document.documentElement.style.setProperty('--tsh-header-height', h + 'px');
        document.documentElement.style.setProperty('--tsh-header-bar-h', Math.round(barH) + 'px');
        if (self.spacer && !self.openRow) self.spacer.style.height = h + 'px';
      };

      this.measure();

      this.querySelectorAll('.tsh-logo img').forEach(function (img) {
        if (!img.complete) self.on(img, 'load', self.measure);
      });

      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(function () { self.measure(); });
        this.ro.observe(this);
        this.cleanup.push(function () { self.ro.disconnect(); });
      } else {
        this.on(window, 'resize', this.measure, { passive: true });
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { self.measure(); });
      }
    }

    /* ======================================================================
       CENTINELA — cambio de estado A→B
       ----------------------------------------------------------------------
       Observamos un elemento real de la página, no un número de píxeles.
       Prioridad:
         1. Elemento marcado con [data-header-sentinel] (lo pone el hero)
         2. Primera sección de la página, si está marcada como superpuesta
         3. Fallback: umbral en píxeles
       ====================================================================== */
    initSentinel() {
      var self = this;

      var apply = function (scrolled) {
        self.classList.toggle('is-scrolled', scrolled);
      };

      // Fallback por scroll: siempre activo como red de seguridad
      var ticking = false;
      var byPixels = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var y = window.scrollY;

          if (!self.sentinelActive) apply(y > self.fallbackPx);

          if (self.hideOnScroll && !self.openRow) {
            self.classList.toggle('is-hidden', y > self.lastY && y > 240);
          } else {
            self.classList.remove('is-hidden');
          }

          self.lastY = y;
          ticking = false;
        });
      };
      this.on(window, 'scroll', byPixels, { passive: true });

      if (!('IntersectionObserver' in window)) { byPixels(); return; }

      var target = this.findSentinel();
      if (!target) { self.sentinelActive = false; byPixels(); return; }

      var observe = function () {
        if (self.io) self.io.disconnect();

        // El margen superior equivale a la altura real de la barra: el estado
        // cambia justo cuando el hero desaparece por detrás del header.
        var barH = self.bar ? Math.round(self.bar.getBoundingClientRect().bottom) : 80;

        self.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            apply(!entry.isIntersecting);
          });
        }, { rootMargin: (-barH) + 'px 0px 0px 0px', threshold: 0 });

        self.io.observe(target);
        self.sentinelActive = true;
      };

      observe();
      this.cleanup.push(function () { if (self.io) self.io.disconnect(); });

      // Al redimensionar cambia la altura de la barra: recalculamos el margen
      var rt;
      this.on(window, 'resize', function () {
        clearTimeout(rt);
        rt = setTimeout(observe, 180);
      }, { passive: true });

      byPixels();
    }

    findSentinel() {
      // 1 · Marcado explícito (el hero lo pone con data-header-sentinel)
      var explicit = document.querySelector('[data-header-sentinel]');
      if (explicit) return explicit;

      // 2 · Primera sección del contenido principal
      var main = document.getElementById('MainContent')
              || document.querySelector('main')
              || document.body;

      var first = main.querySelector('.shopify-section');
      if (!first) return null;

      // Si mide menos que la ventana, no es un hero: no sirve de referencia
      if (first.offsetHeight < window.innerHeight * 0.45) return null;

      return first;
    }

    /* ======================================================================
       FILAS EXPANDIBLES — buscador y menú móvil
       ====================================================================== */
    initRows() {
      var self = this;
      if (!this.rows.length) return;

      var lock = function (on) {
        if (on) {
          var sb = window.innerWidth - document.documentElement.clientWidth;
          if (sb > 0) document.body.style.paddingRight = sb + 'px';
          document.body.classList.add('tsh-locked');
        } else {
          document.body.classList.remove('tsh-locked');
          document.body.style.removeProperty('padding-right');
        }
      };

      this.closeRow = function (silent) {
        if (!self.openRow) return;

        var row = self.openRow;
        row.classList.remove('is-open');

        var trigger = self.querySelector('[data-row-toggle="' + row.dataset.row + '"]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          if (!silent) trigger.focus();
        }

        self.openRow = null;
        self.classList.remove('has-open');
        if (self.veil) self.veil.classList.remove('is-open');
        lock(false);
        self.releaseTrap();

        setTimeout(function () {
          if (!self.openRow && self.veil) self.veil.hidden = true;
          self.measure();
        }, 520);
      };

      this.toggleRow = function (name) {
        var row = self.rows.filter(function (r) { return r.dataset.row === name; })[0];
        if (!row) return;

        if (self.openRow === row) { self.closeRow(); return; }
        if (self.openRow) self.closeRow(true);

        if (self.closeDrops) self.closeDrops(null);

        row.classList.add('is-open');
        self.openRow = row;
        self.classList.add('has-open');
        self.classList.remove('is-hidden');

        var trigger = self.querySelector('[data-row-toggle="' + name + '"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');

        if (self.veil) {
          self.veil.hidden = false;
          requestAnimationFrame(function () { self.veil.classList.add('is-open'); });
        }

        // Solo bloqueamos el scroll con el menú, no con el buscador
        if (name === 'menu') lock(true);

        self.trapFocus(row);

        setTimeout(function () {
          var focusEl = row.querySelector('[data-row-focus]') || row.querySelector(FOCUSABLE);
          if (focusEl) focusEl.focus({ preventScroll: true });
        }, 260);
      };

      this.querySelectorAll('[data-row-toggle]').forEach(function (btn) {
        self.on(btn, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.toggleRow(btn.dataset.rowToggle);
        });
      });

      if (this.veil) this.on(this.veil, 'click', function () { self.closeRow(); });

      this.on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && self.openRow) self.closeRow();
      });

      this.on(document, 'click', function (e) {
        if (!self.openRow) return;
        if (self.contains(e.target)) return;
        self.closeRow(true);
      });

      // Al navegar desde el menú, cerramos
      this.rows.forEach(function (row) {
        row.querySelectorAll('a[href]').forEach(function (a) {
          self.on(a, 'click', function () { self.closeRow(true); });
        });
      });

      // Al pasar a escritorio, el menú móvil no debe quedarse abierto
      var mq = window.matchMedia(MQ_DESKTOP);
      var onChange = function (ev) {
        if (ev.matches && self.openRow && self.openRow.dataset.row === 'menu') self.closeRow(true);
      };
      if (mq.addEventListener) {
        mq.addEventListener('change', onChange);
        this.cleanup.push(function () { mq.removeEventListener('change', onChange); });
      }

      this.safe('searchrow', this.initSearchRow);
      this.safe('menurow',   this.initMenuRow);
    }

    /* ======================================================================
       FILA DE BÚSQUEDA
       ----------------------------------------------------------------------
       Resultados en vivo contra la API de sugerencias de Shopify.

       El parámetro fields es lo que hace que escribir "nik" encuentre Nike:
       por defecto solo se busca en el título del producto, así que una marca
       que no esté escrita en el nombre nunca aparecería. Añadiendo vendor y
       product_type, la búsqueda cubre marca, tipo, variante y SKU.
       ====================================================================== */
    initSearchRow() {
      var self = this;
      var row = this.rows.filter(function (r) { return r.dataset.row === 'search'; })[0];
      if (!row) return;

      var form  = row.querySelector('form');
      var field = row.querySelector('.tsh-searchrow__field');
      var input = row.querySelector('[data-row-focus]');
      var clear = row.querySelector('[data-search-clear]');
      var out   = row.querySelector('[data-results]');
      var quick = row.querySelector('[data-quick]');
      if (!form || !input) return;

      var MIN   = parseInt(row.dataset.minChars, 10) || 2;
      var LIMIT = parseInt(row.dataset.limit, 10) || 6;
      var TXT_ALL  = row.dataset.allLabel || 'Ver todos los resultados';
      var TXT_NONE = row.dataset.noneLabel || 'Sin resultados para';

      var timer = null;
      var controller = null;
      var cache = {};
      var lastQuery = '';

      /* ---------- Utilidades ---------- */
      function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }

      /* Marca en negrita el trozo que coincide con lo escrito */
      function mark(text, q) {
        var safe = esc(text);
        if (!q) return safe;

        var needle = q.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (!needle) return safe;

        try {
          return safe.replace(new RegExp('(' + needle + ')', 'ig'), '<b>$1</b>');
        } catch (e) { return safe; }
      }

      /* Se pide el tamaño exacto al CDN: una foto de 2000px en una
         miniatura de 44 es un desperdicio de ancho de banda */
      function img(url, size) {
        if (!url) return '';
        var sep = url.indexOf('?') > -1 ? '&' : '?';
        return url + sep + 'width=' + size;
      }

      function sync() {
        if (field) field.classList.toggle('has-value', input.value.trim().length > 0);
      }

      /* Los accesos rápidos solo tienen sentido con el campo vacío */
      function showQuick(on) {
        if (quick) quick.style.display = on ? '' : 'none';
      }

      function reset() {
        if (out) out.innerHTML = '';
        showQuick(true);
        lastQuery = '';
      }

      /* ---------- Pintado ---------- */
      function render(data, q) {
        if (!out) return;

        var r = (data && data.resources && data.resources.results) || {};
        var products = r.products || [];
        var collections = r.collections || [];
        var queries = r.queries || [];

        var html = '';

        /* Las sugerencias de texto van primero: son un atajo, no un destino */
        if (queries.length) {
          html += '<p class="tsh-sr__group">Sugerencias</p>';
          queries.slice(0, 3).forEach(function (s) {
            html += '<a class="tsh-sr__item" href="' + esc(s.url) + '" data-sr-item>' +
                      '<span class="tsh-sr__body">' +
                        '<span class="tsh-sr__title">' + mark(s.text, q) + '</span>' +
                      '</span>' +
                    '</a>';
          });
        }

        if (collections.length) {
          html += '<p class="tsh-sr__group">Colecciones</p>';
          collections.slice(0, 4).forEach(function (c) {
            html += '<a class="tsh-sr__item" href="' + esc(c.url) + '" data-sr-item>' +
                      '<span class="tsh-sr__body">' +
                        '<span class="tsh-sr__title">' + mark(c.title, q) + '</span>' +
                      '</span>' +
                    '</a>';
          });
        }

        if (products.length) {
          html += '<p class="tsh-sr__group">Productos</p>';
          products.forEach(function (p) {
            var thumb = p.featured_image && p.featured_image.url
              ? '<img src="' + esc(img(p.featured_image.url, 120)) + '" alt="" width="44" height="44" loading="lazy" decoding="async">'
              : '';

            /* El proveedor como metadato: ayuda a distinguir modelos que
               se llaman igual en marcas distintas */
            var meta = [];
            if (p.vendor) meta.push(esc(p.vendor));
            if (p.price) meta.push(esc(p.price));

            html += '<a class="tsh-sr__item" href="' + esc(p.url) + '" data-sr-item>' +
                      '<span class="tsh-sr__img">' + thumb + '</span>' +
                      '<span class="tsh-sr__body">' +
                        '<span class="tsh-sr__title">' + mark(p.title, q) + '</span>' +
                        (meta.length ? '<span class="tsh-sr__meta">' + meta.join(' · ') + '</span>' : '') +
                      '</span>' +
                    '</a>';
          });
        }

        if (!html) {
          out.innerHTML = '<p class="tsh-sr__note">' + esc(TXT_NONE) + ' «' + esc(q) + '»</p>';
          return;
        }

        html += '<a class="tsh-sr__all" href="' + ROOT + 'search?q=' + encodeURIComponent(q) + '" data-sr-item>' +
                  esc(TXT_ALL) +
                  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                    '<path d="M3 8h10M9 4l4 4-4 4"/>' +
                  '</svg>' +
                '</a>';

        out.innerHTML = html;
      }

      /* ---------- Consulta ---------- */
      function search(q) {
        if (cache[q]) { render(cache[q], q); return; }

        /* Se cancela la petición anterior: al escribir rápido llegaban
           respuestas desordenadas y pintaba la de una letra atrás */
        if (controller) { try { controller.abort(); } catch (e) {} }
        controller = ('AbortController' in window) ? new AbortController() : null;

        var url = ROOT + 'search/suggest.json' +
                  '?q=' + encodeURIComponent(q) +
                  '&resources[type]=product,collection,query' +
                  '&resources[limit]=' + LIMIT +
                  '&resources[options][unavailable_products]=last' +
                  '&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku,tag';

        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            cache[q] = data;
            /* Puede haber llegado tarde: solo se pinta si sigue vigente */
            if (q === lastQuery) render(data, q);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            console.error('[TSH header] búsqueda', err);
          });
      }

      /* ---------- Escritura ---------- */
      this.on(input, 'input', function () {
        sync();

        var q = input.value.trim();
        lastQuery = q;

        if (q.length < MIN) { reset(); return; }

        showQuick(false);
        clearTimeout(timer);
        /* 140ms: suficiente para no lanzar una petición por tecla, poco
           para que se sienta instantáneo */
        timer = setTimeout(function () { search(q); }, 140);
      });

      sync();

      if (clear) {
        this.on(clear, 'click', function (e) {
          e.preventDefault();
          input.value = '';
          sync();
          reset();
          input.focus();
        });
      }

      /* ---------- Teclado ---------- */
      this.on(row, 'keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;

        var items = Array.prototype.slice.call(row.querySelectorAll('[data-sr-item]'));
        if (!items.length) return;

        var current = row.querySelector('[data-sr-item].is-active');
        var i = items.indexOf(current);

        if (e.key === 'Enter') {
          if (current) { e.preventDefault(); current.click(); }
          return;
        }

        e.preventDefault();
        if (current) current.classList.remove('is-active');

        if (e.key === 'ArrowDown') i = (i + 1) % items.length;
        else i = i <= 0 ? items.length - 1 : i - 1;

        items[i].classList.add('is-active');
        items[i].scrollIntoView({ block: 'nearest' });
      });

      /* Al enviar sin nada escrito, el formulario no debe irse a /search */
      this.on(form, 'submit', function (e) {
        if (!input.value.trim()) { e.preventDefault(); input.focus(); }
      });
    }

    /* ---------- Fila de menú móvil ---------- */
    initMenuRow() {
      var self = this;
      var row = this.rows.filter(function (r) { return r.dataset.row === 'menu'; })[0];
      if (!row) return;

      row.querySelectorAll('[data-acc]').forEach(function (btn) {
        self.on(btn, 'click', function () {
          var li = btn.closest('.tsh-menurow__item');
          if (!li) return;
          var open = !li.classList.contains('is-open');

          // Uno abierto a la vez
          row.querySelectorAll('.tsh-menurow__item.is-open').forEach(function (other) {
            if (other === li) return;
            other.classList.remove('is-open');
            var b = other.querySelector('[data-acc]');
            if (b) b.setAttribute('aria-expanded', 'false');
          });

          li.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', String(open));
        });
      });
    }

    /* ======================================================================
       TRAMPA DE FOCO
       ====================================================================== */
    trapFocus(container) {
      this.releaseTrap();

      this.trapHandler = function (e) {
        if (e.key !== 'Tab') return;
        var nodes = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
          .filter(function (n) { return n.offsetParent !== null; });
        if (!nodes.length) return;

        var first = nodes[0];
        var last  = nodes[nodes.length - 1];

        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };

      document.addEventListener('keydown', this.trapHandler);
    }

    releaseTrap() {
      if (this.trapHandler) {
        document.removeEventListener('keydown', this.trapHandler);
        this.trapHandler = null;
      }
    }

    /* ======================================================================
       DROPDOWNS DE ESCRITORIO
       ----------------------------------------------------------------------
       Los paneles se mueven al <body>. Dentro de la píldora no funcionarían:
       su backdrop-filter convierte cualquier position:fixed hijo en relativo
       a ella, y además overflow:hidden lo recortaría.

       Cada panel guarda su referencia en item._panel, porque tras el traslado
       ya no es descendiente del <li>.
       ====================================================================== */
    initDropdowns() {
      var self = this;
      var items = Array.prototype.slice.call(this.querySelectorAll('[data-dropdown]'));
      if (!items.length) return;

      var timer = null;
      this._panels = [];

      var closeItem = function (item) {
        item.classList.remove('is-open');
        var t = item.querySelector('[data-drop-trigger]');
        var p = item._panel;
        if (t) t.setAttribute('aria-expanded', 'false');
        if (p) p.classList.remove('is-open');
      };

      this.closeDrops = function (except) {
        items.forEach(function (it) { if (it !== except) closeItem(it); });
      };

      items.forEach(function (item) {
        var trigger = item.querySelector('[data-drop-trigger]');
        var panel   = item.querySelector('[data-drop-panel]');
        if (!trigger || !panel) return;

        // Fuera de la píldora: si no, el fixed queda atrapado y recortado
        document.body.appendChild(panel);
        item._panel = panel;
        self._panels.push(panel);

        var place = function () {
          var pad = 16;

          /* El mega menú se ancla a la píldora: mismo ancho y misma
             posición siempre, sea cual sea el enlace que lo abre. */
          if (panel.classList.contains('tsh-drop--mega') && self.pill) {
            var pill = self.pill.getBoundingClientRect();
            panel.style.left  = Math.round(pill.left) + 'px';
            panel.style.width = Math.round(pill.width) + 'px';
            panel.style.top   = Math.round(pill.bottom + 12) + 'px';
            return;
          }

          /* Los desplegables simples sí siguen a su enlace */
          var t = trigger.getBoundingClientRect();

          panel.style.removeProperty('width');
          panel.style.visibility = 'hidden';
          panel.style.display = 'block';
          var w = panel.offsetWidth;
          panel.style.removeProperty('display');
          panel.style.removeProperty('visibility');

          var left = t.left - 18;
          left = Math.max(pad, Math.min(left, window.innerWidth - w - pad));

          panel.style.left = Math.round(left) + 'px';
          panel.style.top  = Math.round(t.bottom + 14) + 'px';
        };

        var open = function () {
          clearTimeout(timer);
          self.closeDrops(item);
          place();
          item.classList.add('is-open');
          panel.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        };

        var close = function () { closeItem(item); };

        self.on(item, 'mouseenter', function () { if (isDesktop()) open(); });
        self.on(item, 'mouseleave', function () {
          if (!isDesktop()) return;
          timer = setTimeout(close, 180);
        });

        // El panel está fuera del <li>: mantenerlo abierto al pasar por él
        self.on(panel, 'mouseenter', function () { clearTimeout(timer); });
        self.on(panel, 'mouseleave', function () {
          if (!isDesktop()) return;
          timer = setTimeout(close, 180);
        });

        // Primer clic abre, segundo navega
        self.on(trigger, 'click', function (e) {
          if (!isDesktop()) return;
          if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
        });

        self.on(trigger, 'keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            open();
            var first = panel.querySelector(FOCUSABLE);
            if (first) first.focus();
          } else if (e.key === 'Escape') { close(); trigger.focus(); }
        });

        self.on(panel, 'keydown', function (e) {
          if (e.key === 'Escape') { e.preventDefault(); close(); trigger.focus(); }
        });

        self.on(panel, 'focusout', function (e) {
          if (!panel.contains(e.relatedTarget) && !item.contains(e.relatedTarget)) close();
        });

        // Al navegar desde el panel, cerramos: si no, se queda abierto
        panel.querySelectorAll('a[href]').forEach(function (a) {
          self.on(a, 'click', function () { close(); });
        });

        item._reposition = place;
      });

      var reposition = function () {
        items.forEach(function (it) {
          if (it.classList.contains('is-open') && it._reposition) it._reposition();
        });
      };

      this.on(window, 'scroll', reposition, { passive: true });
      this.on(window, 'resize', function () {
        if (!isDesktop()) self.closeDrops(null);
        else reposition();
      }, { passive: true });

      this.on(document, 'click', function (e) {
        if (self.contains(e.target)) return;
        var inPanel = items.some(function (it) {
          return it._panel && it._panel.contains(e.target);
        });
        if (!inPanel) self.closeDrops(null);
      });
    }

    /* ======================================================================
       IDIOMA
       ====================================================================== */
    initLang() {
      var self = this;
      var wrap = this.querySelector('[data-lang]');
      if (!wrap) return;

      var trigger = wrap.querySelector('[data-lang-trigger]');
      var menu    = wrap.querySelector('.tsh-lang__menu');
      if (!trigger || !menu) return;

      // Mismo motivo que los desplegables: fuera de la píldora
      document.body.appendChild(menu);
      if (!this._panels) this._panels = [];
      this._panels.push(menu);

      var place = function () {
        var t = trigger.getBoundingClientRect();
        menu.style.visibility = 'hidden';
        menu.style.display = 'block';
        var w = menu.offsetWidth;
        menu.style.removeProperty('display');
        menu.style.removeProperty('visibility');

        var left = Math.min(t.right - w, window.innerWidth - w - 16);
        menu.style.left = Math.round(Math.max(16, left)) + 'px';
        menu.style.top  = Math.round(t.bottom + 12) + 'px';
      };

      var close = function () {
        wrap.classList.remove('is-open');
        menu.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };

      this.on(trigger, 'click', function (e) {
        e.stopPropagation();
        var open = !wrap.classList.contains('is-open');
        if (open) { place(); if (self.closeDrops) self.closeDrops(null); }
        wrap.classList.toggle('is-open', open);
        menu.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', String(open));
      });

      this.on(document, 'click', function (e) {
        if (!wrap.contains(e.target) && !menu.contains(e.target)) close();
      });
      this.on(document, 'keydown', function (e) { if (e.key === 'Escape') close(); });
      this.on(window, 'scroll', function () {
        if (wrap.classList.contains('is-open')) place();
      }, { passive: true });
      this.on(window, 'resize', function () {
        if (wrap.classList.contains('is-open')) place();
      }, { passive: true });
    }

    /* ======================================================================
       CARRITO — contador en vivo
       El drawer lo abre Tinker vía on:click="#cart-drawer/open" en el Liquid.
       ====================================================================== */
    initCart() {
      var self = this;
      var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-cart-count]'));
      if (!nodes.length) return;

      this.paintCart = function (count) {
        nodes.forEach(function (node) {
          var span = node.querySelector('span') || node;
          var prev = parseInt(span.textContent, 10) || 0;
          span.textContent = count;
          if (count !== prev) {
            node.classList.remove('is-bumped');
            void node.offsetWidth;
            node.classList.add('is-bumped');
          }
        });
      };

      this.refreshCart = function () {
        fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) { self.paintCart(cart.item_count); })
          .catch(function (err) { console.error('[TSH header] cart', err); });
      };

      ['cart:update', 'cart:refresh', 'cart:change', 'cart:added'].forEach(function (evt) {
        self.on(document, evt, function (e) {
          var d = e.detail || {};
          var c = d.item_count != null ? d.item_count
                : (d.cart && d.cart.item_count != null ? d.cart.item_count : null);
          c != null ? self.paintCart(c) : self.refreshCart();
        });
      });

      // Interceptamos las llamadas al carrito: no dependemos de los eventos del tema
      if (window.fetch && !window.__tshCartPatched) {
        window.__tshCartPatched = true;
        var orig = window.fetch;
        window.fetch = function () {
          var args = arguments;
          var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
          return orig.apply(this, args).then(function (res) {
            if (/\/cart\/(add|change|update|clear)/.test(url)) {
              setTimeout(function () {
                document.dispatchEvent(new CustomEvent('tsh:cart:dirty', { bubbles: true }));
              }, 80);
            }
            return res;
          });
        };
      }

      this.on(document, 'tsh:cart:dirty', function () { self.refreshCart(); });
      this.on(window, 'pageshow', function (e) { if (e.persisted) self.refreshCart(); });
    }

    /* ======================================================================
       FAVORITOS
       ----------------------------------------------------------------------
       El contador se alimenta de tsh:wishlist:changed, que es lo que emite
       el núcleo de la wishlist al añadir o quitar. Antes escuchaba
       tsh:wishlist:toggle, que nadie emite: por eso el número solo cambiaba
       al recargar la página.

       El header NO escribe en el almacenamiento. Antes lo hacía con su
       propio formato —IDs sueltos— y eso habría borrado los objetos
       completos que guarda el núcleo con título, precio e imagen. Aquí solo
       se lee y se pinta; quien manda sobre los datos es TshWishlist.
       ====================================================================== */
    initWishlist() {
      var links = Array.prototype.slice.call(document.querySelectorAll('[data-wishlist]'));
      if (!links.length) return;

      var read = function () {
        /* Si el núcleo está cargado, la cuenta sale de él: entiende el
           formato actual y el antiguo. Leer el almacenamiento a mano es
           solo el respaldo. */
        if (window.TshWishlist && typeof window.TshWishlist.count === 'function') {
          return window.TshWishlist.count();
        }
        try {
          var raw = localStorage.getItem(WISH_KEY);
          var arr = raw ? JSON.parse(raw) : [];
          return Array.isArray(arr) ? arr.length : 0;
        } catch (e) { return 0; }
      };

      var paint = function (n) {
        if (typeof n !== 'number') n = read();

        links.forEach(function (link) {
          link.classList.toggle('has-items', n > 0);

          var badge = link.querySelector('[data-wish-count]');
          if (!badge) return;

          var span = badge.querySelector('span') || badge;
          var prev = parseInt(span.textContent, 10) || 0;
          span.textContent = n > 99 ? '99+' : n;

          /* Mismo rebote que el contador del carrito */
          if (n !== prev) {
            badge.classList.remove('is-bumped');
            void badge.offsetWidth;
            badge.classList.add('is-bumped');
          }
        });
      };

      this.on(document, 'tsh:wishlist:changed', function (e) {
        var d = e.detail || {};
        paint(typeof d.count === 'number' ? d.count : undefined);
      });

      /* El hero y alguna tarjeta antigua emiten todavía el evento viejo.
         El núcleo lo traduce y acaba emitiendo el nuevo, pero se repinta
         aquí también por si el núcleo no estuviera cargado. */
      this.on(document, 'tsh:wishlist:toggle', function () {
        setTimeout(function () { paint(); }, 0);
      });

      /* Entre pestañas */
      this.on(window, 'storage', function (e) {
        if (e.key === WISH_KEY) paint();
      });

      /* Al volver atrás en el navegador la página sale de caché */
      this.on(window, 'pageshow', function (e) {
        if (e.persisted) paint();
      });

      paint();
    }
  }

  customElements.define('tsh-header', TshHeader);
})();